import React, { useEffect, useState, Fragment } from 'react';
import { X, Camera, Plus, Trash2, Check, ChevronDown, ChevronLeft, ChevronRight, AlertCircle, Calendar, Clock, AlertTriangle, User, Building2, ClipboardList, FileText, Zap } from 'lucide-react';
interface NewEsigenzaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (esigenza: any) => void;
  properties: any[];
  preSelectedPropertyId: number | null;
}
type ImpactLevel = 'Basso' | 'Medio' | 'Alto';
const impactDescriptions: Record<ImpactLevel, {
  label: string;
  description: string;
  icon: React.ReactNode;
}> = {
  Basso: {
    label: 'Basso',
    description: 'Il guest potrebbe lamentarsi',
    icon: <User size={16} data-id="element-1021" />
  },
  Medio: {
    label: 'Medio',
    description: 'Il guest sicuramente si lamenterà',
    icon: <AlertTriangle size={16} data-id="element-1022" />
  },
  Alto: {
    label: 'Alto',
    description: 'Compromette radicalmente la permanenza',
    icon: <AlertCircle size={16} data-id="element-1023" />
  }
};
const STEPS = [{
  id: 1,
  label: 'Dettagli',
  icon: FileText
}, {
  id: 2,
  label: 'Priorità',
  icon: AlertTriangle
}, {
  id: 3,
  label: 'Allegati',
  icon: Camera
}, {
  id: 4,
  label: 'Riepilogo',
  icon: ClipboardList
}];
export function NewEsigenzaModal({
  isOpen,
  onClose,
  onSave,
  properties,
  preSelectedPropertyId
}: NewEsigenzaModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    propertyId: '',
    title: '',
    description: '',
    deadlineType: 'check-in' as 'asap' | 'check-in' | 'custom',
    deadlineDate: '',
    impact: 'Medio' as ImpactLevel
  });
  const [checklist, setChecklist] = useState<string[]>([]);
  const [newActivity, setNewActivity] = useState('');
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setFormData(prev => ({
        ...prev,
        propertyId: preSelectedPropertyId ? preSelectedPropertyId.toString() : properties[0]?.id.toString() || ''
      }));
      setChecklist([]);
      setNewActivity('');
    }
  }, [isOpen, preSelectedPropertyId, properties]);
  if (!isOpen) return null;
  const handleAddActivity = () => {
    if (newActivity.trim()) {
      setChecklist([...checklist, newActivity.trim()]);
      setNewActivity('');
    }
  };
  const handleSubmit = () => {
    onSave({
      ...formData,
      checklist,
      property: properties.find(p => p.id.toString() === formData.propertyId),
      createdAt: new Date().toISOString()
    });
  };
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.propertyId && formData.title.trim() && formData.description.trim();
      case 2:
        if (formData.deadlineType === 'custom' && !formData.deadlineDate) return false;
        return true;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return true;
    }
  };
  const goNext = () => {
    if (currentStep < 4 && canProceed()) setCurrentStep(currentStep + 1);
  };
  const goBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const getImpactColor = (level: ImpactLevel) => {
    switch (level) {
      case 'Alto':
        return 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100';
      case 'Medio':
        return 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100';
      case 'Basso':
        return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
    }
  };
  const getImpactRingColor = (level: ImpactLevel) => {
    switch (level) {
      case 'Alto':
        return 'ring-red-300';
      case 'Medio':
        return 'ring-amber-300';
      case 'Basso':
        return 'ring-blue-300';
    }
  };
  const selectedProperty = properties.find(p => p.id.toString() === formData.propertyId);
  // --- Step Content Renderers ---
  const renderStep1 = () => <div className="space-y-5" data-id="element-1024">
      <div data-id="element-1025">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1026">
          Proprietà
        </label>
        <div className="relative" data-id="element-1027">
          <select className="w-full pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none appearance-none bg-white" value={formData.propertyId} onChange={e => setFormData({
          ...formData,
          propertyId: e.target.value
        })} required data-id="element-1028">
            {properties.map(p => <option key={p.id} value={p.id} data-id="element-1029">
                {p.name} - {p.address}
              </option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3 text-gray-400 pointer-events-none" size={16} data-id="element-1030" />
        </div>
      </div>

      <div data-id="element-1031">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1032">
          Titolo Esigenza
        </label>
        <input type="text" required placeholder="es. Pulizia post check-out urgente" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" value={formData.title} onChange={e => setFormData({
        ...formData,
        title: e.target.value
      })} data-id="element-1033" />
      </div>

      <div data-id="element-1034">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1035">
          Descrizione Dettagliata
        </label>
        <textarea required placeholder="Descrivi nel dettaglio cosa serve..." className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none resize-none h-28" value={formData.description} onChange={e => setFormData({
        ...formData,
        description: e.target.value
      })} data-id="element-1036" />
      </div>
    </div>;
  const renderStep2 = () => <div className="space-y-6" data-id="element-1037">
      {/* Deadline */}
      <div className="space-y-3" data-id="element-1038">
        <label className="block text-sm font-medium text-gray-700" data-id="element-1039">
          Deadline
        </label>
        <p className="text-xs text-gray-500 -mt-1" data-id="element-1040">
          Entro quando deve essere risolta questa esigenza?
        </p>
        <div className="space-y-2" data-id="element-1041">
          {/* Option: ASAP */}
          <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${formData.deadlineType === 'asap' ? 'border-teal-deep bg-teal-50/50 ring-1 ring-teal-deep' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1042">
            <input type="radio" name="deadlineType" checked={formData.deadlineType === 'asap'} onChange={() => setFormData({
            ...formData,
            deadlineType: 'asap',
            deadlineDate: ''
          })} className="w-4 h-4 text-teal-deep border-gray-300 focus:ring-teal-deep focus:ring-2" data-id="element-1043" />
            <Zap size={16} className={formData.deadlineType === 'asap' ? 'text-teal-deep' : 'text-gray-400'} data-id="element-1044" />
            <div className="flex-1" data-id="element-1045">
              <span className={`text-sm font-medium ${formData.deadlineType === 'asap' ? 'text-navy' : 'text-gray-600'}`} data-id="element-1046">
                Appena possibile
              </span>
              <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-1047">
                Massima priorità, verrà gestita il prima possibile
              </p>
            </div>
          </label>

          {/* Option: Next check-in */}
          <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${formData.deadlineType === 'check-in' ? 'border-teal-deep bg-teal-50/50 ring-1 ring-teal-deep' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1048">
            <input type="radio" name="deadlineType" checked={formData.deadlineType === 'check-in'} onChange={() => setFormData({
            ...formData,
            deadlineType: 'check-in',
            deadlineDate: ''
          })} className="w-4 h-4 text-teal-deep border-gray-300 focus:ring-teal-deep focus:ring-2" data-id="element-1049" />
            <Calendar size={16} className={formData.deadlineType === 'check-in' ? 'text-teal-deep' : 'text-gray-400'} data-id="element-1050" />
            <div className="flex-1" data-id="element-1051">
              <span className={`text-sm font-medium ${formData.deadlineType === 'check-in' ? 'text-navy' : 'text-gray-600'}`} data-id="element-1052">
                Entro il prossimo check-in
              </span>
              <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-1053">
                La deadline sarà calcolata automaticamente
              </p>
            </div>
          </label>

          {/* Option: Custom date */}
          <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${formData.deadlineType === 'custom' ? 'border-teal-deep bg-teal-50/50 ring-1 ring-teal-deep' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1054">
            <input type="radio" name="deadlineType" checked={formData.deadlineType === 'custom'} onChange={() => setFormData({
            ...formData,
            deadlineType: 'custom'
          })} className="w-4 h-4 text-teal-deep border-gray-300 focus:ring-teal-deep focus:ring-2 mt-0.5" data-id="element-1055" />
            <Clock size={16} className={`mt-0.5 ${formData.deadlineType === 'custom' ? 'text-teal-deep' : 'text-gray-400'}`} data-id="element-1056" />
            <div className="flex-1" data-id="element-1057">
              <span className={`text-sm font-medium ${formData.deadlineType === 'custom' ? 'text-navy' : 'text-gray-600'}`} data-id="element-1058">
                Deadline specifica
              </span>
              {formData.deadlineType === 'custom' && <input type="date" className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none bg-white" value={formData.deadlineDate} onChange={e => setFormData({
              ...formData,
              deadlineDate: e.target.value
            })} required data-id="element-1059" />}
            </div>
          </label>
        </div>
      </div>

      {/* Impact */}
      <div className="space-y-3" data-id="element-1060">
        <label className="block text-sm font-medium text-gray-700" data-id="element-1061">
          Impatto sul Guest
        </label>
        <p className="text-xs text-gray-500 -mt-1" data-id="element-1062">
          Quanto incide questa esigenza sull'esperienza dell'ospite?
        </p>
        <div className="space-y-2" data-id="element-1063">
          {(['Basso', 'Medio', 'Alto'] as ImpactLevel[]).map(level => {
          const info = impactDescriptions[level];
          const isSelected = formData.impact === level;
          const colorClasses = getImpactColor(level);
          const ringColor = getImpactRingColor(level);
          return <button key={level} type="button" onClick={() => setFormData({
            ...formData,
            impact: level
          })} className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${isSelected ? `${colorClasses} ring-1 ${ringColor}` : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-600'}`} data-id="element-1064">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? level === 'Alto' ? 'bg-red-100 text-red-600' : level === 'Medio' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-1065">
                  {info.icon}
                </div>
                <div className="flex-1 min-w-0" data-id="element-1066">
                  <span className={`text-sm font-medium block ${isSelected ? '' : 'text-navy'}`} data-id="element-1067">
                    {info.label}
                  </span>
                  <span className={`text-[11px] block mt-0.5 ${isSelected ? 'opacity-80' : 'text-gray-400'}`} data-id="element-1068">
                    {info.description}
                  </span>
                </div>
                {isSelected && <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${level === 'Alto' ? 'bg-red-600' : level === 'Medio' ? 'bg-amber-600' : 'bg-blue-600'}`} data-id="element-1069">
                    <Check size={12} className="text-white" data-id="element-1070" />
                  </div>}
              </button>;
        })}
        </div>
      </div>
    </div>;
  const renderStep3 = () => <div className="space-y-6" data-id="element-1071">
      {/* Photos */}
      <div className="space-y-3" data-id="element-1072">
        <h3 className="text-sm font-semibold text-navy uppercase tracking-wider" data-id="element-1073">
          Foto (Opzionale)
        </h3>
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors cursor-pointer group" data-id="element-1074">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-white group-hover:shadow-sm transition-all" data-id="element-1075">
            <Camera className="text-gray-400 group-hover:text-teal-deep" size={24} data-id="element-1076" />
          </div>
          <p className="text-sm font-medium text-navy mb-1" data-id="element-1077">Carica foto</p>
          <p className="text-xs text-gray-400" data-id="element-1078">
            Trascina o clicca per caricare
          </p>
        </div>
      </div>

      {/* Checklist */}
      <div className="space-y-3" data-id="element-1079">
        <h3 className="text-sm font-semibold text-navy uppercase tracking-wider" data-id="element-1080">
          Checklist Attività
        </h3>

        <div className="flex gap-2" data-id="element-1081">
          <input type="text" placeholder="Aggiungi un'attività..." className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" value={newActivity} onChange={e => setNewActivity(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddActivity())} data-id="element-1082" />
          <button type="button" onClick={handleAddActivity} className="px-3 py-2.5 bg-gray-100 text-navy rounded-lg hover:bg-gray-200 transition-colors" data-id="element-1083">
            <Plus size={18} data-id="element-1084" />
          </button>
        </div>

        <div className="space-y-2" data-id="element-1085">
          {checklist.map((item, idx) => <div key={idx} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-100" data-id="element-1086">
              <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" data-id="element-1087" />
              <span className="text-sm text-gray-700 flex-1" data-id="element-1088">{item}</span>
              <button type="button" onClick={() => setChecklist(checklist.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-1089">
                <Trash2 size={14} data-id="element-1090" />
              </button>
            </div>)}
          {checklist.length === 0 && <p className="text-xs text-gray-400 italic text-center py-3" data-id="element-1091">
              Nessuna attività aggiunta
            </p>}
        </div>
      </div>
    </div>;
  const renderStep4 = () => {
    const impactInfo = impactDescriptions[formData.impact];
    return <div className="space-y-5" data-id="element-1092">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-1093">
          <AlertCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" data-id="element-1094" />
          <div data-id="element-1095">
            <p className="text-sm font-medium text-blue-900" data-id="element-1096">
              Controlla i dettagli
            </p>
            <p className="text-xs text-blue-700 mt-0.5" data-id="element-1097">
              Verifica che tutte le informazioni siano corrette prima di
              inviare.
            </p>
          </div>
        </div>

        {/* Property & Title */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4" data-id="element-1098">
          <div className="flex items-start gap-3" data-id="element-1099">
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0" data-id="element-1100">
              <Building2 size={16} className="text-teal-deep" data-id="element-1101" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1102">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium" data-id="element-1103">
                Proprietà
              </p>
              <p className="text-sm font-medium text-navy" data-id="element-1104">
                {selectedProperty ? `${selectedProperty.name} - ${selectedProperty.address}` : '—'}
              </p>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4" data-id="element-1105">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1" data-id="element-1106">
              Titolo
            </p>
            <p className="text-sm font-semibold text-navy" data-id="element-1107">
              {formData.title || '—'}
            </p>
          </div>

          <div className="border-t border-gray-100 pt-4" data-id="element-1108">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1" data-id="element-1109">
              Descrizione
            </p>
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1110">
              {formData.description || '—'}
            </p>
          </div>
        </div>

        {/* Deadline & Impact */}
        <div className="grid grid-cols-2 gap-4" data-id="element-1111">
          <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1112">
            <div className="flex items-center gap-2 mb-2" data-id="element-1113">
              <Calendar size={14} className="text-gray-400" data-id="element-1114" />
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium" data-id="element-1115">
                Deadline
              </p>
            </div>
            <p className="text-sm font-medium text-navy" data-id="element-1116">
              {formData.deadlineType === 'asap' ? 'Appena possibile' : formData.deadlineType === 'check-in' ? 'Prossimo check-in' : formData.deadlineDate || '—'}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1117">
            <div className="flex items-center gap-2 mb-2" data-id="element-1118">
              <AlertTriangle size={14} className="text-gray-400" data-id="element-1119" />
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium" data-id="element-1120">
                Impatto
              </p>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${formData.impact === 'Alto' ? 'bg-red-50 text-red-700 border-red-200' : formData.impact === 'Medio' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`} data-id="element-1121">
              {impactInfo.icon}
              {impactInfo.label}
            </span>
          </div>
        </div>

        {/* Checklist Summary */}
        {checklist.length > 0 && <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1122">
            <div className="flex items-center gap-2 mb-3" data-id="element-1123">
              <ClipboardList size={14} className="text-teal-deep" data-id="element-1124" />
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium" data-id="element-1125">
                Checklist ({checklist.length} attività)
              </p>
            </div>
            <div className="space-y-1.5" data-id="element-1126">
              {checklist.map((item, idx) => <div key={idx} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1127">
                  <div className="w-3 h-3 rounded-full border-2 border-gray-300 flex-shrink-0" data-id="element-1128" />
                  <span data-id="element-1129">{item}</span>
                </div>)}
            </div>
          </div>}
      </div>;
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1130">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-1131" />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200" data-id="element-1132">
        {/* Header */}
        <div className="flex-shrink-0 p-6 pb-0 border-b border-gray-100" data-id="element-1133">
          <div className="flex items-center justify-between mb-5" data-id="element-1134">
            <h2 className="text-xl font-bold font-montserrat text-navy" data-id="element-1135">
              Nuova Esigenza
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-navy transition-colors" data-id="element-1136">
              <X size={20} data-id="element-1137" />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-1 pb-5" data-id="element-1138">
            {STEPS.map((step, index) => {
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            const StepIcon = step.icon;
            return <Fragment key={step.id} data-id="element-1139">
                  <button onClick={() => {
                if (isCompleted) setCurrentStep(step.id);
              }} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${isActive ? 'bg-teal-50 text-teal-deep border border-teal-deep/30' : isCompleted ? 'text-teal-deep hover:bg-teal-50/50 cursor-pointer' : 'text-gray-400 cursor-default'}`} data-id="element-1140">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${isActive ? 'bg-teal-deep text-white' : isCompleted ? 'bg-teal-deep text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-1141">
                      {isCompleted ? <Check size={12} data-id="element-1142" /> : step.id}
                    </div>
                    <span className="hidden sm:inline" data-id="element-1143">{step.label}</span>
                  </button>
                  {index < STEPS.length - 1 && <div className={`flex-1 h-px ${currentStep > step.id ? 'bg-teal-deep' : 'bg-gray-200'}`} data-id="element-1144" />}
                </Fragment>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-1145">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex items-center justify-between" data-id="element-1146">
          <div data-id="element-1147">
            {currentStep > 1 ? <button onClick={goBack} className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-navy hover:bg-white rounded-lg border border-gray-200 transition-colors" data-id="element-1148">
                <ChevronLeft size={16} data-id="element-1149" />
                Indietro
              </button> : <button onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-white hover:text-navy transition-colors" data-id="element-1150">
                Annulla
              </button>}
          </div>

          <div className="flex items-center gap-3" data-id="element-1151">
            <span className="text-xs text-gray-400" data-id="element-1152">
              {currentStep} di {STEPS.length}
            </span>
            {currentStep < 4 ? <button onClick={goNext} disabled={!canProceed()} className={`flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${canProceed() ? 'bg-teal-deep text-white hover:bg-teal-deep/90' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-1153">
                Avanti
                <ChevronRight size={16} data-id="element-1154" />
              </button> : <button onClick={handleSubmit} className="px-6 py-2.5 bg-teal-deep text-white rounded-lg text-sm font-medium hover:bg-teal-deep/90 transition-colors shadow-sm flex items-center gap-2" data-id="element-1155">
                <AlertCircle size={16} data-id="element-1156" />
                Crea Esigenza
              </button>}
          </div>
        </div>
      </div>
    </div>;
}